/**
 * Scoring Engine for Assessments
 * Calculates scores and generates insights based on responses
 */

// Nutrition scoring thresholds (currently unused - kept for future reference)
// const NUTRITION_THRESHOLDS = {
//   excellent: 2.5,  // Average score >= 2.5
//   good: 2.0,       // Average score >= 2.0
//   fair: 1.5,       // Average score >= 1.5
//   poor: 0          // Average score < 1.5
// };

// Category weights for nutrition (currently unused - kept for future reference)
// const NUTRITION_CATEGORIES = {
//   energy_food_quality: { weight: 1.2, name: 'Sustained Energy' },
//   protein_frequency: { weight: 1.3, name: 'Muscle Repair' },
//   hydration_during_sport: { weight: 1.2, name: 'Hydration' },
//   post_workout_recovery: { weight: 1.3, name: 'Recovery' },
//   bone_health_foods: { weight: 1.1, name: 'Bone Health' },
//   junk_food_frequency: { weight: 1.0, name: 'Diet Quality' },
//   sleep_quality: { weight: 1.4, name: 'Sleep' },
//   vitamins_minerals: { weight: 1.0, name: 'Vitamins & Minerals' },
//   meal_timing: { weight: 1.0, name: 'Meal Timing' },
//   sugar_intake: { weight: 1.0, name: 'Sugar Control' },
//   fat_quality: { weight: 1.0, name: 'Healthy Fats' },
//   hydration_overall: { weight: 1.1, name: 'Daily Hydration' },
//   food_quality: { weight: 1.0, name: 'Food Quality' },
//   supplementation: { weight: 0.8, name: 'Supplementation' },
//   digestion: { weight: 0.9, name: 'Digestion' },
//   meal_planning: { weight: 0.9, name: 'Meal Planning' },
//   nutrition_knowledge: { weight: 0.9, name: 'Nutrition Knowledge' }
// };

/**
 * Calculate nutrition score using MTM Engine Architecture
 * Scores: training_load, hydration, sleep_recovery, diet_quality, junk_food, overall_readiness
 */
export const calculateNutritionScore = (responses, personalInfo) => {
  if (!responses || responses.length === 0) {
    return null;
  }

  // Group responses by category for component scores
  const categoryScores = {};
  const categoryDetails = {};

  responses.forEach(response => {
    const category = response.category;
    if (!categoryScores[category]) {
      categoryScores[category] = [];
      categoryDetails[category] = [];
    }
    categoryScores[category].push(response.score);
    categoryDetails[category].push({
      question: response.questionId,
      answer: response.answer,
      score: response.score
    });
  });

  // Calculate category averages
  const categoryAverages = {};
  Object.keys(categoryScores).forEach(category => {
    const scores = categoryScores[category];
    categoryAverages[category] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  });

  // MTM Architecture: Calculate component scores (0-100)
  const componentScores = {
    training_load: calculateTrainingLoad(categoryAverages),
    hydration: calculateHydrationScore(categoryAverages),
    sleep_recovery: calculateSleepRecoveryScore(categoryAverages),
    diet_quality: calculateDietQualityScore(categoryAverages),
    junk_food: calculateJunkFoodScore(categoryAverages)
  };

  // Overall Readiness Score (weighted average)
  const overall_readiness = Math.round(
    0.30 * componentScores.training_load +
    0.20 * componentScores.hydration +
    0.25 * componentScores.sleep_recovery +
    0.25 * componentScores.diet_quality
  );

  // Convert overall readiness to 1-3 scale for backward compatibility
  const overallScore = 1 + (overall_readiness / 50); // 0-100 → 1-3
  const percentage = overall_readiness;

  // Determine rating based on overall readiness
  let rating, ratingColor;
  if (overall_readiness >= 80) {
    rating = 'Excellent';
    ratingColor = 'green';
  } else if (overall_readiness >= 65) {
    rating = 'Good';
    ratingColor = 'blue';
  } else if (overall_readiness >= 50) {
    rating = 'Fair';
    ratingColor = 'yellow';
  } else {
    rating = 'Needs Improvement';
    ratingColor = 'red';
  }

  // Identify strengths and weaknesses from component scores
  const strengths = [];
  const weaknesses = [];

  Object.entries(componentScores).forEach(([component, score]) => {
    const componentName = component.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    
    if (score >= 70) {
      strengths.push({
        category: componentName,
        score: score,
        percentage: score
      });
    } else if (score < 55) {
      weaknesses.push({
        category: componentName,
        score: score,
        percentage: score
      });
    }
  });

  // Sort by score
  strengths.sort((a, b) => b.score - a.score);
  weaknesses.sort((a, b) => a.score - b.score);

  // Risk flags
  const riskFlags = [];
  if (componentScores.sleep_recovery < 60) riskFlags.push('possible_under_recovery');
  if (componentScores.junk_food < 50) riskFlags.push('junk_food_concern');
  if (componentScores.hydration < 60) riskFlags.push('hydration_risk');

  return {
    overallScore: overallScore.toFixed(2),
    percentage: Math.round(percentage),
    rating,
    ratingColor,
    componentScores,      // MTM Architecture: 0-100 scores
    overall_readiness,    // MTM Architecture: weighted score
    riskFlags,           // MTM Architecture: risk indicators
    categoryScores: categoryAverages,  // Backward compatibility
    categoryDetails,
    strengths: strengths.slice(0, 3),
    weaknesses: weaknesses.slice(0, 3),
    totalResponses: responses.length
  };
};

// MTM Architecture: Component score calculators
function calculateTrainingLoad(categoryAverages) {
  // Based on training frequency, duration, intensity from responses
  // Simplified: use average of relevant categories
  const relevantCategories = ['energy_food_quality', 'post_workout_recovery', 'meal_timing'];
  let total = 0;
  let count = 0;
  
  relevantCategories.forEach(cat => {
    if (categoryAverages[cat]) {
      total += categoryAverages[cat];
      count++;
    }
  });
  
  const avg = count > 0 ? total / count : 2;
  return Math.round(((avg - 1) / 2) * 100); // Convert 1-3 to 0-100
}

function calculateHydrationScore(categoryAverages) {
  const hydrationCats = ['hydration_during_sport', 'hydration_overall', 'hydration_minerals'];
  let total = 0;
  let count = 0;
  
  hydrationCats.forEach(cat => {
    if (categoryAverages[cat]) {
      total += categoryAverages[cat];
      count++;
    }
  });
  
  const avg = count > 0 ? total / count : 2;
  return Math.round(((avg - 1) / 2) * 100);
}

function calculateSleepRecoveryScore(categoryAverages) {
  const sleepScore = categoryAverages['sleep_quality'] || 2;
  const recoveryScore = categoryAverages['post_workout_recovery'] || 2;
  
  const avg = (sleepScore + recoveryScore) / 2;
  return Math.round(((avg - 1) / 2) * 100);
}

function calculateDietQualityScore(categoryAverages) {
  const qualityCats = ['food_quality', 'meal_frequency', 'vitamins_minerals', 'protein_frequency'];
  let total = 0;
  let count = 0;
  
  qualityCats.forEach(cat => {
    if (categoryAverages[cat]) {
      total += categoryAverages[cat];
      count++;
    }
  });
  
  const avg = count > 0 ? total / count : 2;
  return Math.round(((avg - 1) / 2) * 100);
}

function calculateJunkFoodScore(categoryAverages) {
  const junkScore = categoryAverages['junk_food_frequency'] || 2;
  // Higher score = less junk food = better
  return Math.round(((junkScore - 1) / 2) * 100);
}

/**
 * Calculate cognitive/psychometric profile using MTM Engine Architecture
 * Traits are counted and normalized to 0-100 scale
 */
export const calculateCognitiveProfile = (responses) => {
  if (!responses || responses.length === 0) {
    return null;
  }

  // Initialize all trait counters (MTM Architecture - 24 traits)
  const traitCounts = {
    // Thinking & Personality
    methodical: 0, experimental: 0, analytical: 0, collaborative: 0,
    structured: 0, flexible: 0, extravert: 0, introvert: 0,
    // Emotion & Coping
    empathetic: 0, resilient: 0, anxious: 0, avoidant: 0,
    problem_focused: 0, persistent: 0, growth_mindset: 0, fixed_mindset: 0,
    // Sensory & Environment
    noise_sensitive: 0, visual_sensitive: 0,
    // Social & Work Style
    social: 0, autonomous: 0, help_seeking: 0,
    // Learning Preferences
    visual_learner: 0, kinesthetic: 0, reading_learner: 0, auditory: 0
  };

  const categoryScores = {};

  responses.forEach(response => {
    // Count traits (each answer contributes +1 to its trait)
    if (response.trait) {
      if (traitCounts.hasOwnProperty(response.trait)) {
        traitCounts[response.trait] += 1;
      } else {
        traitCounts[response.trait] = 1;
      }
    }

    // Category scores (for backward compatibility)
    const category = response.category;
    if (!categoryScores[category]) {
      categoryScores[category] = [];
    }
    categoryScores[category].push(response.score);
  });

  // Calculate maximum possible for each trait (24 questions max)
  const maxPossible = 24;

  // Normalize traits to 0-100 scale
  const normalizedTraits = {};
  Object.keys(traitCounts).forEach(trait => {
    const rawCount = traitCounts[trait];
    // Normalize: (count / maxPossible) * 100
    normalizedTraits[trait] = Math.round((rawCount / maxPossible) * 100);
  });

  // Calculate category averages (for backward compatibility)
  const categoryAverages = {};
  Object.keys(categoryScores).forEach(category => {
    const scores = categoryScores[category];
    categoryAverages[category] = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  });

  // Identify dominant traits (top 5 by normalized score)
  const dominantTraits = Object.entries(normalizedTraits)
    .filter(([_, score]) => score > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([trait, score]) => ({
      trait,
      score,
      percentage: score,
      interpretation: score >= 67 ? 'High' : score >= 34 ? 'Medium' : 'Low'
    }));

  // Personality profile
  const profile = {
    // Introvert vs Extravert
    socialStyle: determineSocialStyle(traitCounts),
    
    // Learning style
    learningStyle: determineLearningStyle(categoryAverages),
    
    // Stress response
    stressResponse: determineStressResponse(categoryAverages),
    
    // Leadership style
    leadershipStyle: determineLeadershipStyle(categoryAverages),
    
    // Work style
    workStyle: determineWorkStyle(categoryAverages),
    
    // Motivation
    motivationDriver: determineMotivation(categoryAverages)
  };

  return {
    dominantTraits,
    normalizedTraits,  // MTM Architecture: 0-100 normalized scores
    traitCounts,       // Raw counts for debugging
    categoryAverages,
    profile,
    totalResponses: responses.length
  };
};

// Helper functions for cognitive profile
function determineSocialStyle(traitCounts) {
  const introvertScore = (traitCounts.introvert || 0) + (traitCounts.reserved || 0);
  const extravertScore = (traitCounts.extravert || 0) + (traitCounts.expressive || 0);
  
  if (introvertScore > extravertScore) {
    return { type: 'Introvert', description: 'Energized by quiet time and reflection' };
  } else if (extravertScore > introvertScore) {
    return { type: 'Extravert', description: 'Energized by social interaction and activity' };
  } else {
    return { type: 'Ambivert', description: 'Balanced between introversion and extraversion' };
  }
}

function determineLearningStyle(categoryAverages) {
  const detailScore = categoryAverages.learning_style || 2;
  
  if (detailScore >= 2.5) {
    return { type: 'Hands-On Learner', description: 'Learn best by doing and practicing' };
  } else if (detailScore >= 2.0) {
    return { type: 'Visual Learner', description: 'Learn best by seeing the big picture' };
  } else {
    return { type: 'Step-by-Step Learner', description: 'Learn best with detailed instructions' };
  }
}

function determineStressResponse(categoryAverages) {
  const stressScore = categoryAverages.stress_response || 2;
  
  if (stressScore >= 2.5) {
    return { type: 'Stress Positive', description: 'Thrives under pressure and challenges' };
  } else if (stressScore >= 2.0) {
    return { type: 'Balanced', description: 'Handles stress with moderate effectiveness' };
  } else {
    return { type: 'Stress Sensitive', description: 'Benefits from structured support during pressure' };
  }
}

function determineLeadershipStyle(categoryAverages) {
  const leadershipScore = categoryAverages.leadership || 2;
  
  if (leadershipScore >= 2.5) {
    return { type: 'Natural Leader', description: 'Takes initiative and organizes others' };
  } else if (leadershipScore >= 2.0) {
    return { type: 'Supportive Team Player', description: 'Helps and supports the team' };
  } else {
    return { type: 'Collaborative Follower', description: 'Works well following guidance' };
  }
}

function determineWorkStyle(categoryAverages) {
  const workScore = categoryAverages.work_style || 2;
  
  if (workScore >= 2.5) {
    return { type: 'Improvisational', description: 'Flexible and adapts on the go' };
  } else if (workScore >= 2.0) {
    return { type: 'Balanced Planner', description: 'Mix of planning and flexibility' };
  } else {
    return { type: 'Structured Planner', description: 'Prefers detailed plans and organization' };
  }
}

function determineMotivation(categoryAverages) {
  const motivationScore = categoryAverages.motivation || 2;
  
  if (motivationScore >= 2.5) {
    return { type: 'Achievement Oriented', description: 'Driven by winning and goals' };
  } else if (motivationScore >= 2.0) {
    return { type: 'Mastery Oriented', description: 'Driven by skill improvement' };
  } else {
    return { type: 'Team Oriented', description: 'Driven by team success' };
  }
}

/**
 * Generate recommendations based on scores
 */
export const generateRecommendations = (nutritionScore, cognitiveProfile) => {
  const recommendations = [];

  // Nutrition recommendations
  if (nutritionScore) {
    nutritionScore.weaknesses.forEach(weakness => {
      recommendations.push({
        type: 'nutrition',
        category: weakness.category,
        priority: 'high',
        recommendation: getNutritionRecommendation(weakness.category, weakness.score)
      });
    });
  }

  // Cognitive recommendations
  if (cognitiveProfile) {
    recommendations.push({
      type: 'cognitive',
      category: 'Learning',
      priority: 'medium',
      recommendation: getCognitiveRecommendation(cognitiveProfile.profile)
    });
  }

  return recommendations;
};

function getNutritionRecommendation(category, score) {
  const recommendations = {
    'Sustained Energy': 'Switch to whole grains like brown rice, atta roti, and millets (ragi, bajra) for better energy throughout the day.',
    'Muscle Repair': 'Include protein in both lunch and dinner - dal, paneer, eggs, chicken, or sprouts.',
    'Hydration': 'Drink water consistently before, during, and after training. Add nimbu pani or coconut water for electrolytes.',
    'Recovery': 'Eat within 30-60 minutes after training - try banana with milk, eggs, or chana for best recovery.',
    'Bone Health': 'Increase calcium intake with 2-3 servings of milk, dahi, paneer, or ragi daily.',
    'Diet Quality': 'Reduce junk food to once a week or less. Choose fruits, nuts, and yogurt as snacks instead.',
    'Sleep': 'Aim for 8+ hours of quality sleep. Avoid screens before bed and maintain a consistent sleep schedule.',
    'Vitamins & Minerals': 'Eat more colorful fruits and vegetables - aim for 5 servings daily.',
    'Meal Timing': 'Eat meals at consistent times each day, including a healthy breakfast every morning.',
    'Sugar Control': 'Limit sugary drinks and packaged juices. Choose water, nimbu pani, or coconut water instead.',
    'Healthy Fats': 'Include healthy fats like nuts, seeds, ghee, and fish in your regular meals.',
    'Daily Hydration': 'Drink 8+ glasses of water daily, more on training days.',
    'Food Quality': 'Focus on home-cooked meals with fresh ingredients whenever possible.',
    'Supplementation': 'Consult with a nutritionist about appropriate supplements for your training needs.',
    'Digestion': 'Eat slowly, chew well, and avoid heavy meals close to training time.',
    'Meal Planning': 'Plan your meals ahead of time to ensure balanced nutrition throughout the week.',
    'Nutrition Knowledge': 'Learn more about sports nutrition to make better food choices for your performance.'
  };

  return recommendations[category] || 'Focus on improving this area of your nutrition.';
}

function getCognitiveRecommendation(profile) {
  const learningStyle = profile.learningStyle.type;
  const socialStyle = profile.socialStyle.type;
  
  return `As a ${learningStyle} with ${socialStyle} tendencies, you'll excel with ${
    learningStyle.includes('Hands-On') ? 'practical drills and active practice' :
    learningStyle.includes('Visual') ? 'demonstrations and strategic discussions' :
    'clear step-by-step instructions and structured training'
  }. ${
    socialStyle === 'Extravert' ? 'Team activities and group training will energize you.' :
    socialStyle === 'Introvert' ? 'Individual practice with focused coaching will help you thrive.' :
    'Balance solo practice with team activities for best results.'
  }`;
}
